import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import {
  Tag, CheckCircle2, Clock, ShoppingBag, RefreshCw,
  Sparkles, Copy, Check, Ticket, Wallet, Gift,
  AlertCircle, ArrowRight, Star, Shield
} from 'lucide-react';
import { API_URL } from '../config/api';

/* ─── helpers ─────────────────────────────────────────────────── */
const MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];

function monthLabel(index) {
  const base = new Date(2026, 0, 1);
  base.setMonth(base.getMonth() + (index - 1));
  return `${MONTHS[base.getMonth()]} '${String(base.getFullYear()).slice(2)}`;
}

function validUntil(index) {
  const base = new Date(2026, 0, 1);
  base.setMonth(base.getMonth() + (index - 1) + 24);
  return `${MONTHS[base.getMonth()]} ${base.getFullYear()}`;
}

/* ─── Status config ────────────────────────────────────────────── */
const STATUS_CONFIG = {
  Paid: {
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #6366f1 100%)',
    labelBg: '#10b981',
    label: 'READY TO USE',
    textColor: '#fff',
    glowColor: 'rgba(124,58,237,.25)',
    dotColor: '#10b981',
  },
  Redeemed: {
    gradient: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
    labelBg: '#475569',
    label: 'USED',
    textColor: '#fff',
    glowColor: 'rgba(100,116,139,.15)',
    dotColor: '#94a3b8',
  },
  Unpaid: {
    gradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 50%, #fbbf24 100%)',
    labelBg: '#64748b',
    label: 'LOCKED',
    textColor: '#fff',
    glowColor: 'rgba(249,115,22,.15)',
    dotColor: '#fbbf24',
  },
};

/* ─── Copy button ──────────────────────────────────────────────── */
const CopyButton = ({ text, disabled }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handleCopy}
      disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: disabled ? 'rgba(255,255,255,.1)' : 'rgba(255,255,255,.2)',
        border: '1px solid rgba(255,255,255,.3)',
        borderRadius: 8, padding: '4px 10px',
        fontSize: 11, fontWeight: 700, color: disabled ? 'rgba(255,255,255,.4)' : '#fff',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'all .2s', backdropFilter: 'blur(4px)',
        letterSpacing: '.3px',
      }}
    >
      {copied ? <Check size={11} /> : <Copy size={11} />}
      {copied ? 'Copied!' : 'Copy Code'}
    </button>
  );
};

/* ─── Single Coupon Ticket ─────────────────────────────────────── */
const CouponTicket = ({ coupon, onRedeem, animDelay }) => {
  const cfg = STATUS_CONFIG[coupon.status] || STATUS_CONFIG.Unpaid;
  const isPaid = coupon.status === 'Paid';
  const isRedeemed = coupon.status === 'Redeemed';

  return (
    <div style={{
      position: 'relative',
      borderRadius: 18,
      overflow: 'hidden',
      boxShadow: `0 8px 32px ${cfg.glowColor}, 0 2px 8px rgba(0,0,0,.08)`,
      transition: 'transform .25s cubic-bezier(.34,1.56,.64,1), box-shadow .25s',
      animationDelay: `${animDelay}ms`,
      opacity: isRedeemed ? 0.7 : 1,
    }}
      onMouseEnter={e => { if (!isRedeemed) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 16px 48px ${cfg.glowColor}, 0 4px 16px rgba(0,0,0,.1)`; }}}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 8px 32px ${cfg.glowColor}, 0 2px 8px rgba(0,0,0,.08)`; }}
    >
      {/* Redeemed diagonal stamp */}
      {isRedeemed && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 10, pointerEvents: 'none',
        }}>
          <div style={{
            transform: 'rotate(-30deg)',
            border: '3px solid rgba(255,255,255,.5)',
            borderRadius: 6, padding: '4px 20px',
            fontSize: 22, fontWeight: 900, color: 'rgba(255,255,255,.6)',
            letterSpacing: 4, textTransform: 'uppercase',
            backdropFilter: 'blur(2px)',
          }}>USED</div>
        </div>
      )}

      <div style={{ display: 'flex', minHeight: 140 }}>

        {/* LEFT — white stub with vertical code */}
        <div style={{
          position: 'relative',
          width: 90, minWidth: 90,
          background: 'rgba(255,255,255,.95)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 6,
        }}>
          {/* notches left */}
          {[...Array(10)].map((_, i) => (
            <div key={`nl-${i}`} style={{
              position: 'absolute', left: -7, top: `${(i + 1) * 9}%`,
              width: 14, height: 14, borderRadius: '50%',
              background: 'linear-gradient(135deg,#fef9f0,#fff7ed)',
              border: '1px solid rgba(249,115,22,.15)', flexShrink: 0,
            }} />
          ))}
          {/* notches right (divider) */}
          {[...Array(10)].map((_, i) => (
            <div key={`nr-${i}`} style={{
              position: 'absolute', right: -7, top: `${(i + 1) * 9}%`,
              width: 14, height: 14, borderRadius: '50%',
              background: cfg.gradient.includes('7c3aed')
                ? 'rgba(124,58,237,.08)'
                : cfg.gradient.includes('64748b')
                  ? 'rgba(100,116,139,.08)'
                  : 'rgba(249,115,22,.08)',
              border: '1px solid rgba(255,255,255,.4)', flexShrink: 0,
            }} />
          ))}

          {/* Month label */}
          <span style={{
            fontSize: 10, fontWeight: 800, color: '#94a3b8',
            letterSpacing: 1, textTransform: 'uppercase',
          }}>{monthLabel(coupon.month_index)}</span>

          {/* Vertical coupon code */}
          <span style={{
            fontSize: 11, fontWeight: 900, color: '#1e293b',
            fontFamily: 'monospace', letterSpacing: 2,
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)',
          }}>{coupon.code}</span>

          {/* Month number badge */}
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: 'linear-gradient(135deg,rgba(249,115,22,.15),rgba(249,115,22,.05))',
            border: '1px solid rgba(249,115,22,.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 900, color: '#f97316',
          }}>{coupon.month_index}</div>
        </div>

        {/* RIGHT — gradient coloured body */}
        <div style={{
          flex: 1,
          background: cfg.gradient,
          display: 'flex', flexDirection: 'column',
          justifyContent: 'space-between',
          padding: '14px 18px',
          position: 'relative', overflow: 'hidden',
        }}>
          {/* shimmer gloss */}
          <div style={{
            position: 'absolute', top: 0, left: '-40%', width: '60%', height: '100%',
            background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,.08) 50%, transparent 70%)',
            pointerEvents: 'none',
          }} />

          {/* Status badge */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'rgba(0,0,0,.2)', backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255,255,255,.2)',
              borderRadius: 20, padding: '3px 10px',
              fontSize: 9, fontWeight: 800, color: '#fff',
              letterSpacing: 1, textTransform: 'uppercase',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.dotColor }} />
              {cfg.label}
            </span>
            <CopyButton text={coupon.code} disabled={!isPaid} />
          </div>

          {/* Amount */}
          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.7)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>
              Savings Reward
            </div>
            <div style={{
              fontSize: 38, fontWeight: 900, color: '#fff', lineHeight: 1,
              textShadow: '0 2px 12px rgba(0,0,0,.2)',
            }}>
              ₹{(coupon.amount || 1000).toLocaleString('en-IN')}
            </div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,.6)', marginTop: 3 }}>
              Valid until {validUntil(coupon.month_index)}
            </div>
          </div>

          {/* Action row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
            <span style={{
              fontSize: 10, color: 'rgba(255,255,255,.55)',
              fontFamily: 'monospace', letterSpacing: .5,
            }}>
              BrightBazaar · Month {coupon.month_index}/20
            </span>

            {isRedeemed && (
              <span style={{
                fontSize: 10, color: 'rgba(255,255,255,.5)', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: .5,
              }}>
                <CheckCircle2 size={12} style={{ verticalAlign: 'middle', marginRight: 3 }} />
                Already Used
              </span>
            )}
            {coupon.status === 'Unpaid' && (
              <span style={{
                fontSize: 10, color: 'rgba(255,255,255,.6)', fontWeight: 700,
                display: 'flex', alignItems: 'center', gap: 4,
              }}>
                <Clock size={11} /> Pay installment to unlock
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ─────────────────────────────────────────────────── */
const CouponsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [stats, setStats] = useState({ paid: 0, unpaid: 0, redeemed: 0, total: 0 });
  const [dbError, setDbError] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user) {
      loadCoupons();
    }
  }, [user]);

  const loadCoupons = async () => {
    console.log('🔍 [CouponsPage] Starting loadCoupons for user:', user?.id);
    try {
      setLoading(true);
      
      // 1. Fetch Profile first
      let profile = null;
      try {
        const { data: profData, error: profErr } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        if (profErr) console.warn('⚠️ [CouponsPage] Profile fetch error:', profErr);
        profile = profData;
        console.log('👤 [CouponsPage] Fetched user profile:', profile);
      } catch (e) {
        console.warn('⚠️ [CouponsPage] Profile fetch exception:', e);
      }

      // Format customer code (e.g. '1800' from 'BPV-1800')
      const rawId = profile?.customer_id || profile?.card_no || user?.customer_id || user?.id || '1800';
      const cleanId = rawId.toString().replace('#', '').replace('BPV-', '').replace('BPV', '').trim().toUpperCase() || '1800';
      console.log('🏷️ [CouponsPage] Derived clean customer code:', cleanId);

      // 2. Try Primary Backend API Endpoint
      try {
        console.log('🚀 [CouponsPage] Requesting GET', `${API_URL}/api/coupons/user/${user.id}`);
        const res = await fetch(`${API_URL}/api/coupons/user/${user.id}`);
        console.log('📡 [CouponsPage] Backend response status:', res.status);
        if (res.ok) {
          const contentType = res.headers.get('content-type') || '';
          if (contentType.includes('application/json')) {
            const data = await res.json();
            console.log('📦 [CouponsPage] Backend API data received:', data);
            if (data.success && data.coupons && data.coupons.length > 0) {
              setCoupons(data.coupons);
              updateStats(data.coupons);
              return;
            }
          }
        }
      } catch (apiErr) {
        console.warn('⚠️ [CouponsPage] Backend API fetch failed:', apiErr);
      }

      // 3. Direct Supabase Query for Payments (checking valid UUID candidate keys)
      const isUuid = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
      
      const searchKeys = Array.from(new Set([
        user.id,
        profile?.customer_id,
        profile?.id
      ].filter(key => isUuid(key))));

      console.log('🔎 [CouponsPage] Searching customer_payments with candidate UUID keys:', searchKeys);

      let paidCount = 0;
      for (const key of searchKeys) {
        try {
          const { data: payments, error: payErr } = await supabase
            .from('customer_payments')
            .select('status, payment_amount')
            .eq('customer_id', key);

          if (payErr) {
            console.warn(`⚠️ [CouponsPage] Payments query error for key "${key}":`, payErr);
          } else if (payments && payments.length > 0) {
            const paidList = payments.filter(p => {
              const st = (p.status || '').toLowerCase();
              return st === 'paid' || st === 'success' || st === 'completed';
            });
            console.log(`💳 [CouponsPage] Key "${key}" matched ${payments.length} total payments, ${paidList.length} paid payments.`);
            if (paidList.length > paidCount) {
              paidCount = paidList.length;
            }
          }
        } catch (e) {
          console.warn(`⚠️ [CouponsPage] Exception querying payments for key "${key}":`, e);
        }
      }

      console.log('📊 [CouponsPage] Final verified paid installments count:', paidCount);

      if (paidCount === 0 && profile) {
        paidCount = profile.paid_months || profile.paid_installments || profile.months_paid || 0;
        console.log('📊 [CouponsPage] Fallback profile paid_months count:', paidCount);
      }

      // 4. Check ecom_coupons for redeemed status
      let redeemedMonths = new Set();
      for (const key of searchKeys) {
        try {
          const { data: dbCoupons } = await supabase
            .from('ecom_coupons')
            .select('month_index, status')
            .eq('user_id', key);
          
          if (dbCoupons) {
            dbCoupons.filter(c => c.status === 'Redeemed').forEach(c => redeemedMonths.add(c.month_index));
          }
        } catch (e) {}
      }

      // 5. Generate 20 milestone coupons
      const generated = Array.from({ length: 20 }, (_, i) => {
        const monthIndex = i + 1;
        const monthStr = monthIndex.toString().padStart(2, '0');
        let status = 'Unpaid';

        if (redeemedMonths.has(monthIndex)) {
          status = 'Redeemed';
        } else if (monthIndex <= paidCount) {
          status = 'Paid';
        }

        return {
          user_id: user.id,
          code: `BVP${cleanId}CPN${monthStr}`,
          month_index: monthIndex,
          amount: 1000,
          status: status,
          name: `Month ${monthIndex} Savings Reward`
        };
      });

      console.log('✨ [CouponsPage] Generated milestone coupons:', generated);
      setCoupons(generated);
      updateStats(generated);
    } catch (err) {
      console.error('❌ [CouponsPage] Load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (data) => {
    const s = { paid: 0, unpaid: 0, redeemed: 0, total: data.length };
    data.forEach(c => { if (c.status === 'Redeemed') s.redeemed++; else if (c.status === 'Paid') s.paid++; else s.unpaid++; });
    setStats(s);
  };

  const handleSync = async () => {
    console.log('🔄 [CouponsPage] Sync requested for user:', user?.id);
    try {
      setSyncing(true);
      const res = await fetch(`${API_URL}/api/coupons/sync/${user.id}`, {
        method: 'POST', 
        headers: { 'Content-Type': 'application/json' }
      });
      console.log('📡 [CouponsPage] Sync response status:', res.status);
      if (res.ok) {
        const contentType = res.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          const syncData = await res.json();
          console.log('📦 [CouponsPage] Sync response data:', syncData);
        }
      }
      await loadCoupons();
    } catch (err) {
      console.warn('⚠️ [CouponsPage] Sync exception:', err);
      await loadCoupons();
    } finally {
      setSyncing(false);
    }
  };

  const handleRedeem = (coupon) => {
    if (coupon.status !== 'Paid') return;
    navigator.clipboard.writeText(coupon.code);
    navigate(`/cart?coupon=${coupon.code}`);
  };

  const filteredCoupons = coupons.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'available') return c.status === 'Paid';
    if (filter === 'locked') return c.status === 'Unpaid';
    if (filter === 'used') return c.status === 'Redeemed';
    return true;
  });

  return (
    <div style={s.pageWrap}>
      <div style={s.orb1} />
      <div style={s.orb2} />
      <div style={s.orb3} />

      <div style={s.container}>

        {/* ── Page Header ── */}
        <div style={s.header}>
          <div>
            <div style={s.headerBadge}>
              <Ticket size={12} /> Database Verified Coupons
            </div>
            <h1 style={s.pageTitle}>My Coupons</h1>
            <p style={s.pageSubtitle}>
              BrightBazaar 20-Month Savings Plan — ₹1,000 reward per paid installment
            </p>
          </div>
          <button
            onClick={handleSync}
            disabled={syncing}
            style={{
              ...s.syncBtn,
              opacity: syncing ? .6 : 1,
              cursor: syncing ? 'not-allowed' : 'pointer',
            }}
          >
            <RefreshCw size={14} style={{ animation: syncing ? 'spin .8s linear infinite' : 'none' }} />
            {syncing ? 'Syncing…' : 'Sync Ledger'}
          </button>
        </div>

        {/* ── DB Error Banner ── */}
        {dbError && (
          <div style={s.warnBanner}>
            <AlertCircle size={16} color="#d97706" style={{ flexShrink: 0 }} />
            <span>Running in local mode — database table not yet set up. Coupons are loaded from your device.</span>
          </div>
        )}

        {/* ── Stats Bento Grid ── */}
        <div style={s.statsGrid}>
          {[
            { label: 'Available', val: stats.paid, icon: Gift, grad: 'linear-gradient(135deg,#7c3aed,#a855f7)', note: 'Ready to use at checkout' },
            { label: 'Locked', val: stats.unpaid, icon: Clock, grad: 'linear-gradient(135deg,#f97316,#fb923c)', note: 'Pay installment to unlock' },
            { label: 'Used', val: stats.redeemed, icon: CheckCircle2, grad: 'linear-gradient(135deg,#10b981,#059669)', note: 'Redeemed successfully' },
            { label: 'Total', val: stats.total, icon: Wallet, grad: 'linear-gradient(135deg,#3b82f6,#6366f1)', note: '20-Month Plan milestones' },
          ].map((card, i) => {
            const Icon = card.icon;
            return (
              <div key={i} style={s.statCard}>
                <div style={{ ...s.statIcon, background: card.grad }}>
                  <Icon size={18} color="#fff" />
                </div>
                <div>
                  <div style={s.statVal}>{card.val}</div>
                  <div style={s.statLabel}>{card.label}</div>
                  <div style={s.statNote}>{card.note}</div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Savings Value Banner ── */}
        {stats.paid > 0 && (
          <div style={s.savingsBanner}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Star size={20} color="#f97316" />
              <div>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#1e293b' }}>
                  You have <span style={{ color: '#7c3aed' }}>₹{(stats.paid * 1000).toLocaleString('en-IN')}</span> in ready-to-use coupon value!
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Apply your BVP coupons at checkout to save instantly</div>
              </div>
            </div>
            <Link to="/" style={s.shopNowBtn}>
              Shop Now <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {/* ── Filter Tabs ── */}
        <div style={s.filterRow}>
          {[
            { key: 'all', label: `All (${stats.total})` },
            { key: 'available', label: `Available (${stats.paid})` },
            { key: 'locked', label: `Locked (${stats.unpaid})` },
            { key: 'used', label: `Used (${stats.redeemed})` },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                ...s.filterTab,
                background: filter === f.key
                  ? 'linear-gradient(135deg,#7c3aed,#a855f7)'
                  : 'rgba(255,255,255,.8)',
                color: filter === f.key ? '#fff' : '#64748b',
                fontWeight: filter === f.key ? 800 : 600,
                boxShadow: filter === f.key ? '0 4px 14px rgba(124,58,237,.3)' : 'none',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* ── Content ── */}
        {loading ? (
          <div style={s.loadingWrap}>
            <div style={s.spinner} />
            <p style={{ color: '#64748b', marginTop: 20, fontWeight: 600 }}>Loading your coupon ledger…</p>
          </div>
        ) : filteredCoupons.length === 0 ? (
          <div style={s.emptyCard}>
            <div style={s.emptyIcon}><Shield size={40} color="#a855f7" /></div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', margin: '0 0 8px' }}>
              {filter === 'all' ? 'No Coupons Yet' : `No ${filter} coupons`}
            </h3>
            <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 20px' }}>
              {filter === 'all'
                ? 'Enroll in the BrightBazaar 20-Month Savings Plan to start earning monthly coupons.'
                : 'Try switching to a different filter above.'}
            </p>
            {filter !== 'all' ? (
              <button onClick={() => setFilter('all')} style={s.primaryBtn}>View All Coupons</button>
            ) : (
              <button onClick={handleSync} style={s.primaryBtn}><RefreshCw size={14} /> Sync Coupons</button>
            )}
          </div>
        ) : (
          <div style={s.couponGrid}>
            {filteredCoupons.map((coupon, i) => (
              <CouponTicket
                key={coupon.id || `cpn-${coupon.month_index}`}
                coupon={coupon}
                onRedeem={handleRedeem}
                animDelay={i * 50}
              />
            ))}
          </div>
        )}

        {/* ── How it works ── */}
        <div style={s.howItWorksCard}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: '#1e293b', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sparkles size={16} color="#f97316" /> How Coupons Work
          </h3>
          <div style={s.stepsGrid}>
            {[
              { n: '01', title: 'Pay Installment', desc: 'Each monthly plan payment unlocks 1 coupon worth ₹1,000' },
              { n: '02', title: 'Coupon Unlocked', desc: 'Your coupon becomes "Available" automatically after payment' },
              { n: '03', title: 'Apply at Checkout', desc: 'Use the coupon code during checkout to get instant discount' },
            ].map(step => (
              <div key={step.n} style={s.step}>
                <div style={s.stepNum}>{step.n}</div>
                <div style={s.stepTitle}>{step.title}</div>
                <div style={s.stepDesc}>{step.desc}</div>
              </div>
            ))}
          </div>
        </div>

        <p style={s.footer}>BrightBazaar Authentication Protocol · Authorized Vouchers Only · BrightPlanet Ventures</p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

/* ─── styles ─────────────────────────────────────────────────── */
const s = {
  pageWrap: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fdf4ff 0%, #fef9f0 35%, #eff6ff 70%, #f0fdf4 100%)',
    position: 'relative', overflow: 'hidden', paddingBottom: 80,
  },
  orb1: { position: 'fixed', top: -120, right: -120, width: 550, height: 550, borderRadius: '50%', background: 'radial-gradient(circle, rgba(168,85,247,.2) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  orb2: { position: 'fixed', bottom: -150, left: -100, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(249,115,22,.14) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },
  orb3: { position: 'fixed', top: '50%', left: '40%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,.08) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 },

  container: { maxWidth: 920, margin: '0 auto', padding: '40px 20px', position: 'relative', zIndex: 1 },

  header: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 16, marginBottom: 28,
  },
  headerBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    background: 'rgba(168,85,247,.12)', border: '1px solid rgba(168,85,247,.25)',
    borderRadius: 20, padding: '4px 12px', fontSize: 11,
    fontWeight: 700, color: '#7c3aed', marginBottom: 10, letterSpacing: '.5px',
    textTransform: 'uppercase',
  },
  pageTitle: {
    fontSize: 'clamp(26px,5vw,36px)', fontWeight: 900, margin: 0, lineHeight: 1.1,
    background: 'linear-gradient(135deg,#1e293b 0%,#7c3aed 100%)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  pageSubtitle: { fontSize: 13, color: '#64748b', marginTop: 6 },

  syncBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    background: 'rgba(255,255,255,.85)', backdropFilter: 'blur(12px)',
    border: '1.5px solid rgba(168,85,247,.25)', borderRadius: 14,
    padding: '10px 20px', fontSize: 13, fontWeight: 700, color: '#7c3aed',
    boxShadow: '0 4px 16px rgba(168,85,247,.12)', alignSelf: 'flex-start',
    transition: 'all .2s',
  },

  warnBanner: {
    display: 'flex', alignItems: 'center', gap: 10,
    background: 'rgba(254,243,199,.9)', backdropFilter: 'blur(12px)',
    border: '1px solid rgba(217,119,6,.25)', borderRadius: 14,
    padding: '12px 16px', marginBottom: 20,
    fontSize: 12, color: '#92400e', fontWeight: 600,
  },

  /* stats */
  statsGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))',
    gap: 14, marginBottom: 20,
  },
  statCard: {
    background: 'rgba(255,255,255,.82)', backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255,255,255,.8)', borderRadius: 18,
    padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 14,
    boxShadow: '0 4px 20px rgba(0,0,0,.06)',
  },
  statIcon: { width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  statVal: {
    fontSize: 26, fontWeight: 900,
    background: 'linear-gradient(135deg,#1e293b,#7c3aed)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  statLabel: { fontSize: 11, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: '.5px' },
  statNote: { fontSize: 10, color: '#94a3b8', marginTop: 1 },

  /* savings banner */
  savingsBanner: {
    background: 'rgba(255,255,255,.88)', backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(249,115,22,.2)', borderRadius: 18,
    padding: '16px 22px', marginBottom: 22,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: 12,
    boxShadow: '0 4px 20px rgba(249,115,22,.08)',
  },
  shopNowBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'linear-gradient(135deg,#f97316,#ea580c)',
    color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'none',
    padding: '9px 20px', borderRadius: 12,
    boxShadow: '0 4px 14px rgba(249,115,22,.3)', flexShrink: 0,
  },

  /* filter tabs */
  filterRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 24 },
  filterTab: {
    padding: '8px 16px', borderRadius: 20, border: 'none',
    fontSize: 12, cursor: 'pointer', transition: 'all .2s',
    backdropFilter: 'blur(12px)',
  },

  /* loading / empty */
  loadingWrap: { textAlign: 'center', padding: '80px 0' },
  spinner: {
    width: 44, height: 44, margin: '0 auto',
    border: '3px solid rgba(168,85,247,.2)', borderTopColor: '#a855f7',
    borderRadius: '50%', animation: 'spin .8s linear infinite',
  },
  emptyCard: {
    background: 'rgba(255,255,255,.82)', backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255,255,255,.8)', borderRadius: 24,
    padding: '64px 32px', textAlign: 'center',
    boxShadow: '0 8px 32px rgba(0,0,0,.06)',
  },
  emptyIcon: {
    width: 80, height: 80, borderRadius: '50%',
    background: 'linear-gradient(135deg,rgba(168,85,247,.15),rgba(168,85,247,.05))',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 20px',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 7,
    background: 'linear-gradient(135deg,#7c3aed,#a855f7)',
    color: '#fff', fontWeight: 700, fontSize: 13, border: 'none',
    padding: '10px 22px', borderRadius: 12, cursor: 'pointer',
    boxShadow: '0 4px 16px rgba(124,58,237,.3)',
  },

  /* coupon grid */
  couponGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(340px,1fr))',
    gap: 20, marginBottom: 36,
  },

  /* how it works */
  howItWorksCard: {
    background: 'rgba(255,255,255,.82)', backdropFilter: 'blur(20px)',
    border: '1.5px solid rgba(255,255,255,.8)', borderRadius: 22,
    padding: '24px 28px', marginBottom: 24,
    boxShadow: '0 4px 20px rgba(0,0,0,.05)',
  },
  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16 },
  step: {
    background: 'rgba(248,250,252,.9)', border: '1px solid rgba(226,232,240,.8)',
    borderRadius: 16, padding: '16px',
  },
  stepNum: {
    fontSize: 28, fontWeight: 900, lineHeight: 1, marginBottom: 8,
    background: 'linear-gradient(135deg,#7c3aed,#f97316)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
  },
  stepTitle: { fontSize: 13, fontWeight: 800, color: '#1e293b', marginBottom: 4 },
  stepDesc: { fontSize: 12, color: '#64748b', lineHeight: 1.5 },

  footer: {
    textAlign: 'center', fontSize: 10, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: 1.5, marginTop: 8,
  },
};

export default CouponsPage;
